import { Link } from 'react-router-dom'
import { useSolicitacoesDaProximaJanela } from '../../api/queries/cab'
import { EtiquetaDePrioridade } from '../../components/Etiqueta'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { TEXTOS } from '../../lib/glossario'
import type { ResumoGmud } from '../../types/gmud'

/**
 * As solicitações que entram na próxima janela, abaixo da data dela.
 *
 * Pedido da contratada no Teste de uso 1: quem executa quer saber, olhando o calendário, o que vai
 * a produção na próxima abertura — sem abrir a lista e filtrar por estado.
 *
 * **Dois grupos, e não uma lista só.** A aprovada já pode ir a produção a partir da abertura; a que
 * está com o comitê ainda depende da decisão do dia. Misturar as duas faria a segunda parecer
 * liberada. E a frase de rodapé é parte da tela: o sistema não agenda solicitação em janela, e sem
 * dizer isso a lista pareceria uma agenda marcada — com a que aguarda pauta esquecida fora dela.
 *
 * @returns o bloco abaixo da data da próxima janela
 */
export function SolicitacoesDaProximaJanela() {
  const { data, isPending, isError, error } = useSolicitacoesDaProximaJanela()
  const textos = TEXTOS.cab.calendario.naJanela

  if (isPending) {
    return <Esqueleto linhas={2} className="ml-9 mt-2" />
  }
  if (isError) {
    return (
      <div className="ml-9 mt-2">
        <AvisoDeErro erro={error} titulo={textos.naoCarregou} />
      </div>
    )
  }

  const vazia = data.aprovadas.length === 0 && data.emAvaliacao.length === 0

  return (
    <section aria-label={textos.titulo} className="ml-9 mt-2 rounded-lg bg-slate-50 px-3 py-2.5">
      <h3 className="text-xs font-semibold text-slate-700">{textos.titulo}</h3>
      {vazia ? (
        <p className="mt-1 text-sm text-slate-600">{textos.nenhuma}</p>
      ) : (
        <>
          <Grupo titulo={textos.aprovadas} itens={data.aprovadas} />
          <Grupo titulo={textos.emAvaliacao} itens={data.emAvaliacao} />
        </>
      )}
      <p className="mt-2 max-w-prose text-xs text-slate-500">{textos.comoEntra}</p>
    </section>
  )
}

/** Um grupo da janela. Grupo vazio não aparece: o outro já diz o que há. */
function Grupo({ titulo, itens }: { titulo: string; itens: ResumoGmud[] }) {
  if (itens.length === 0) {
    return null
  }
  return (
    <div className="mt-2">
      <p className="text-xs text-slate-600">{titulo}</p>
      <ul className="mt-1 space-y-1" aria-label={titulo}>
        {itens.map((gmud) => (
          <li key={gmud.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <Link to={`/gmuds/${gmud.id}`} className="font-mono font-semibold text-niteroi-800 hover:underline">
              {gmud.codigo}
            </Link>
            <EtiquetaDePrioridade prioridade={gmud.prioridade} />
            <span className="text-slate-700">{[gmud.sistema, gmud.finalidade].filter(Boolean).join(' · ')}</span>
            {gmud.chamadoNumero && <span className="text-xs text-slate-500">chamado {gmud.chamadoNumero}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}
